import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { z } from 'zod';
import { permissions } from 'shared/constants/permissions';
import { useCopilotFrontendTool, zodToolSchema } from '../hooks/useCopilotFrontendTool';
import { COPILOT_NAVIGATION_TARGETS, findCopilotNavigationTarget } from './navigationCatalog';
interface ChangeUrlToolParams {
  targetId: string;
  replace?: boolean;
}
export type ChangeUrlDecision = 'approved' | 'rejected';
export interface ChangeUrlToolResult {
  success: boolean;
  status: ChangeUrlDecision | 'pending' | 'failed';
  message: string;
  replace?: boolean;
  target?: {
    id: string;
    label: string;
    path: string;
  };
  availableTargets?: Array<{
    id: string;
    label: string;
    description: string;
  }>;
}

/**
 * Registers the `change_url` frontend tool for allowlisted in-app navigation.
 *
 * Validates `targetId` against the navigation catalog and returns a pending
 * result without navigating. The chat card collects Approve/Reject, then
 * navigates (on approve) and resumes the agent via `sendToolResult`.
 * `followUp: false` keeps the agent paused until that resume.
 */
export const useChangeUrlTool = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = COPILOT_NAVIGATION_TARGETS.map(_temp).join(", ");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const availableTargetsDescription = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      name: "change_url",
      description: `Propose navigating within the Alvie app by choosing one of the allowlisted route ids. Available targets: ${availableTargetsDescription}. The user must approve before the page changes — explain why the destination helps, then invoke this tool via call_frontend_tool. When you need the full product description for a target before proposing navigation, first invoke get_navigation_target_details via call_frontend_tool. Do not invent URLs.`,
      requiredPermission: permissions.TAKE_ALL_ACTIONS,
      followUp: false,
      parameters: zodToolSchema(z.object({
        targetId: z.string().describe("The route id to navigate to. Must be one of the available target ids."),
        replace: z.boolean().describe("Whether to replace the current browser history entry instead of pushing a new one.").optional()
      })),
      handler: _temp3
    };
    t2 = [availableTargetsDescription];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useCopilotFrontendTool(t1, t2);
};
function _temp(target) {
  return `${target.id}: ${target.label}`;
}
function _temp2(t0) {
  const {
    id,
    label,
    description
  } = t0;
  return {
    id,
    label,
    description
  };
}
async function _temp3(params) {
  const target_0 = findCopilotNavigationTarget(params.targetId);
  if (target_0 == null) {
    return {
      success: false,
      status: "failed",
      message: `Unknown navigation target "${params.targetId}".`,
      availableTargets: COPILOT_NAVIGATION_TARGETS.map(_temp2)
    };
  }
  return {
    success: true,
    status: "pending",
    message: `Awaiting confirmation to navigate to ${target_0.label}.`,
    replace: params.replace === true,
    target: {
      id: target_0.id,
      label: target_0.label,
      path: target_0.path
    }
  };
}